import { Injectable } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, mergeMap, tap, retryWhen, delayWhen, take } from 'rxjs/operators';
import { timer } from 'rxjs';

/* definitions */
import { authRedirectionPaths } from 'src/app/features/shared/definitions';
import { settings } from 'src/definitions/settings';

/* services */
import { LiifeCoreService, TokenService } from 'src/app/features/auth/services';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* router data store actions */
import * as RouterDataStoreActions from '../../router-data-store/state/router-data-store.actions';

/* auth data store actions */
import * as AuthDataStoreActions from './auth-data-store.actions';
import { environment } from '@environment';
import { AuthControllerService as CoreAuthControllerService, LoginRequestControllerService } from 'src/app/features/shared/core-sdk/services';

import { } from 'src/app/features/shared/core-sdk/services';
import { User } from 'src/app/features/shared/sdk/models';

@Injectable()
export class AuthDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private tokenService: TokenService,
    private liifeCoreService: LiifeCoreService,
    private loginRequestControllerService: LoginRequestControllerService,
    private coreAuthControllerService: CoreAuthControllerService
  ) { }

  postTokenProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GetTokenByCredentialsSuccessful),
      mergeMap((action) => {
        return [
          {
            type: AuthDataStoreActions.ActionTypes.SetToken,
            payload: {
              token: action.payload.token,
            },
          },
          {
            type: AuthDataStoreActions.ActionTypes.LoginByToken,
            payload: {
              token: action.payload.token,
              redirectUrl: action.payload.redirectUrl,
            },
          },
        ];
      }),
    ),
  );

  loginByToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoginByToken),
      mergeMap((action) => {
        const isTokenExist = !!action.payload.token;
        const actions: any[] = [];
        if (isTokenExist) {

          return this.coreAuthControllerService.loginByAuthorizationToken({}).pipe(
            mergeMap((user: User) => {

              if (user) {
                actions.push(
                  {
                    type: AuthDataStoreActions.ActionTypes.SetUser,
                    payload: { user: user },
                  });

                if (action.payload?.redirectUrl) {
                  actions.push({
                    type: RouterDataStoreActions.ActionTypes.Navigate,
                    payload: { path: action.payload?.redirectUrl },
                  });
                }
              }

              return actions;

            }),
            catchError((error) => {

              return [
                {
                  type: AuthDataStoreActions.ActionTypes.Logout,
                },
              ];
            })
          );

        } else {
          actions.push(
            {
              type: AuthDataStoreActions.ActionTypes.Logout
            });
        }
        return actions;
      }),
    ),
  );


  loginByTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoginByTokenFailure),
      mergeMap((action) => [{ type: AuthDataStoreActions.ActionTypes.Logout }]),
    ),
  );

  setToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthDataStoreActions.SetToken),
        tap((action) => {
          const token = action.payload.token;
          this.tokenService.setToken(token);
        }),
      ),
    { dispatch: false },
  );

  clearToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthDataStoreActions.ClearToken),
        tap((action) => {
          this.tokenService.clearToken();
        }),
      ),
    { dispatch: false },
  );

  loggedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoggedIn),
      mergeMap((action) => {
        const redirectUrl = action.payload.redirectUrl;
        const sessionToken = localStorage.getItem('DS');
        const refreshToken = localStorage.getItem('DSR');
        const actions: any[] = [
          {
            type: AuthDataStoreActions.ActionTypes.SetUser,
            payload: { user: action.payload.user },
          },
          {
            type: AuthDataStoreActions.ActionTypes.LoginByDescopeTokenWithoutDevice,
            payload: {
              sessionToken: sessionToken,
              refreshToken: refreshToken,
              redirectUrl: redirectUrl ?? ''
            },
          },
          {
            type: AuthDataStoreActions.ActionTypes.GenerateLoginRequest
          }
        ];
        if (redirectUrl) {
          actions.push({
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: redirectUrl },
          });
        }
        return actions;
      }),
    ),
  );

  generateLoginRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GenerateLoginRequest),
      mergeMap((action) => {
        const tenantPublicKey = environment.adminTenantPublicKey;
        const sessionToken = localStorage.getItem('DS');
        const refreshToken = localStorage.getItem('DSR');
        return this.loginRequestControllerService.create({
          body: {
            descopeRefreshToken: refreshToken,
            descopeSessionToken: sessionToken
          }
        }).pipe(
          mergeMap((res) => {
            const loginRequestId = res.id;
            return [
              {
                type: AuthDataStoreActions.ActionTypes.GenerateLoginRequestCreated,
                payload: {
                  loginRequestId: loginRequestId,
                  loginRequestMessage: res.message,
                  loginRequestData: res,
                },
              },
            ];
          }),
          catchError((error) => {
            return [
              { type: AuthDataStoreActions.ActionTypes.Logout },
            ];
          }),
        );
      }),
    ),
  );

  // poll this.coreAuthControllerService.loginByApprovedLoginRequest({id: action.payload.loginRequestId, body: action.payload.loginRequesData}) every 2 second until it returns a success response


  pollLoginRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.GenerateLoginRequestCreated),
      mergeMap((action: any) => {
        const loginRequestId = action.payload.loginRequestId;
        const loginRequestMessage = action.payload.loginRequestMessage;
        const loginRequestData = action.payload.loginRequestData;

        if (!loginRequestId && !loginRequestMessage) {
          return [
            { type: AuthDataStoreActions.ActionTypes.Logout },
          ];
        } else {
          // Poll the login request every 5 seconds
          // and retry up to 60 times
          return this.coreAuthControllerService.loginByApprovedLoginRequest$Response({
            id: loginRequestId,
            body: {
              message: loginRequestMessage
            },
          }).pipe(
            retryWhen(errors =>
              errors.pipe(
                delayWhen(() => timer(5000)), // Wait 5 seconds before retrying
                take(60) // Retry up to 60 times
              )
            ),
            mergeMap((res) => {
              if (res) {

                // this is core token, saved to local storage
                const token = res.headers?.get('Authorization') || '';
                if (token) {
                  localStorage.setItem('Authorization', token);

                  // const user = res.body;
                  // console.log(user);

                  // if (user.email /* && user.tenant?.integratedLoginProviders?.includes('google') */) {
                  //   setTimeout(() => {
                  //     const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
                  //     url.searchParams.set('client_id', environment.googleAuthClientID);
                  //     url.searchParams.set('scope', environment.googleAuthScope);
                  //     url.searchParams.set('redirect_uri', environment.googleAuthRedirectURI);
                  //     url.searchParams.set('response_type', 'code');
                  //     url.searchParams.set('access_type', 'offline');
                  //     url.searchParams.set('login_hint', user.email);
                  //     url.searchParams.set('prompt', 'consent');
                  //     url.searchParams.set('state', localStorage.getItem('Authorization'));
                  //     window.location.href = url.toString();
                  //   }, 2000);
                  // }

                  return [
                    {
                      type: AuthDataStoreActions.ActionTypes.SetToken,
                      payload: { token: token },
                    },
                    {
                      type: AuthDataStoreActions.ActionTypes.SetUser,
                      payload: { user: res },
                    },
                    {
                      type: AuthDataStoreActions.ActionTypes.GenerateLoginRequestSuccessful,
                      payload: {
                        loginRequestId: action.payload?.loginRequestId,
                        loginRequestMessage: action.payload?.loginRequestMessage,
                        loginRequestData: action.payload?.loginRequestData,
                      },
                    },
                    {
                      type: RouterDataStoreActions.ActionTypes.Navigate,
                      payload: { path: authRedirectionPaths.login.afterLoginRedirectionPath },
                    },
                  ];
                } else {
                  // no token
                  return [
                    {
                      type: AuthDataStoreActions.ActionTypes.Logout,
                    },
                  ];
                }
              } else {
                // no response
                return [
                  { type: AuthDataStoreActions.ActionTypes.Logout },
                ];
              }
            }),
            catchError((error) => {
              if (error.status === 401) {
                return [
                  { type: AuthDataStoreActions.ActionTypes.Logout },
                ];
              }
              return [];
            })
          );
        }

        return [];
      }),
    ),
  );

  loginByDescopeTokenWithoutDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.LoginByDescopeTokenWithoutDevice),
      mergeMap((action) => {
        const tenantPublicKey = environment.adminTenantPublicKey;
        const sessionToken = action.payload.sessionToken;
        const refreshToken = action.payload.refreshToken;
        const redirectUrl = action.payload.redirectUrl;
        return this.liifeCoreService.loginByDescopeTokenWithoutDevice(tenantPublicKey, sessionToken, refreshToken).pipe(
          mergeMap((res) => {
            const coreToken = res.headers['authorization'];
            if (coreToken) {
              localStorage.setItem('liifeCoreToken', coreToken);
            }
            const actions: any[] = [
              {
                type: AuthDataStoreActions.ActionTypes
                  .LoginByDescopeTokenWithoutDeviceSuccessful,
                payload: {
                  sessionToken: action.payload.sessionToken,
                  refreshToken: action.payload.refreshToken,
                  redirectUrl: redirectUrl ?? '',
                  coreToken: coreToken,
                },
              },
            ];
            return actions;
          }),
          catchError((error) => {
            return [
              { type: AuthDataStoreActions.ActionTypes.Logout },
            ];
          }),
        );
      }),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.Logout),
      mergeMap((action) => {
        return [
          { type: AuthDataStoreActions.ActionTypes.ClearToken },
          {
            type: AuthDataStoreActions.ActionTypes.SetUser,
            payload: { user: null },
          },
          {
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: '/auth' },
          },
        ];
      }),
    ),
  );

  navigateToLoginPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.NavigateToLoginPage),
      mergeMap((action) => {
        return [
          {
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: '/auth/login' },
          },
        ];
      }),
    ),
  );

  navigateToRegisterPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthDataStoreActions.NavigateToRegisterPage),
      mergeMap((action) => {
        return [
          {
            type: RouterDataStoreActions.ActionTypes.Navigate,
            payload: { path: '/auth/register' },
          },
        ];
      }),
    ),
  );
}
